import _isString from "lodash/isString";
import _isUndefined from "lodash/isUndefined";
import _isPlainObject from "lodash/isPlainObject";
import _isArray from "lodash/isArray";
import "core-js/modules/es6.array.map.js";
import "core-js/modules/es6.object.keys.js";

/**
 * Deeply maps the matching keys of the source object to the new key names specified in the field mapping.
 * @param source - The source object that will have its keys mapped.
 * @param fieldMapping - The field mapping to apply to the source object.
 * @returns A new object with keys matching a mapping field remapped and all others unchanged.
 *
 * @example
 * ```ts
 * const obj = { key: { nestedKey: true }};
 * const simpleMapping = { key: 'newKey' };
 *
 * mapKeysDeep(obj, simpleMapping);
 * // { newKey: { nestedKey: true } }
 *
 * const nestedMapping = { key: ['newKey', { nestedKey: 'newNestedKey' }] };
 * mapKeysDeep(obj, nestedMapping);
 * // { newKey: { newNestedKey: true } }
 * ```
 */
var mapKeysDeep = function mapKeysDeep(obj, mapping) {
  if (!obj || !mapping) {
    return obj;
  }

  if (_isArray(obj)) {
    return obj.map(function (objIter) {
      return mapKeysDeep(objIter, mapping);
    });
  }

  if (_isPlainObject(obj)) {
    return Object.keys(obj).reduce(function (acc, key) {
      var mappingEntry = mapping[key];

      if (!_isUndefined(mappingEntry)) {
        if (_isString(mappingEntry)) {
          acc[mappingEntry] = obj[key];
          return acc;
        } // If you are remapping a nested object but also want to remap the key of the property
        // indexing into that nested object, then you can pass the new key name and the nested mapping
        // in a tuple ['newKeyName', {}]


        if (_isArray(mappingEntry)) {
          acc[mappingEntry[0]] = mapKeysDeep(obj[key], mappingEntry[1]);
          return acc;
        }

        if (_isPlainObject(mappingEntry)) {
          acc[key] = mapKeysDeep(obj[key], mappingEntry);
          return acc;
        } // If you need to a remove a key from the object, you can map it to undefined

      } else if (Object.prototype.hasOwnProperty.call(mapping, key)) {
        return acc;
      }

      acc[key] = obj[key];
      return acc;
    }, {});
  }

  return obj;
};

export default mapKeysDeep;